import { useEffect, useState } from "react";
import styled from "styled-components";
import WebApp from "@twa-dev/sdk";
import { IoChevronForward, IoMoon, IoLanguage, IoVolumeMedium, IoMusicalNotes } from "react-icons/io5";
import { FaShoppingCart, FaLink, FaUsers, FaCrown } from 'react-icons/fa';
import { APP_NAME } from '../constants/app';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { useSoundContext } from "../contexts/SoundContext";
import useUserStore from '../stores/userStore';
import useScrollToTop from '../hooks/useScrollToTop';

const API_URL = process.env.REACT_APP_API_URL;
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

// Скелетон-блок для универсального использования
const SkeletonBlock = styled.div`
	background: rgba(200,200,200,0.15);
	border-radius: 8px;
	min-height: ${props => props.height || '24px'};
	min-width: ${props => props.width || '40px'};
	width: ${props => props.width || '100%'};
	height: ${props => props.height || '24px'};
	margin: 0 auto;
	animation: skeleton-loading 1.2s infinite linear;
	@keyframes skeleton-loading {
		0% { opacity: 0.5; }
		50% { opacity: 1; }
		100% { opacity: 0.5; }
	}
`;

const AvatarOuter = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 104px;
	height: 104px;
`;

const AvatarContainer = styled.div`
	width: 96px;
	height: 96px;
	margin-bottom: 16px;
	border-radius: 50%;
	overflow: hidden;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	position: relative;
	display: inline-block;
	border: ${props => props.$premium ? '2px solid #fff8' : 'none'};
`;

const Avatar = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const DefaultAvatar = styled.div`
	width: 100%;
	height: 100%;
	background: ${props => props.theme.primary};
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 36px;
	font-weight: 600;
`;

const VipBadge = styled.div`
	position: absolute;
	left: 50%;
	bottom: 8px;
	top: auto;
	transform: translateX(-50%);
	background: linear-gradient(90deg, #FFD700 0%, #FFB300 100%);
	color: #fff;
	font-size: 13px;
	font-weight: bold;
	padding: 2px 12px 2px 8px;
	border-radius: 16px;
	box-shadow: 0 2px 8px #0003;
	z-index: 2;
	letter-spacing: 1px;
	border: 1.5px solid #fff7;
	display: flex;
	align-items: center;
	gap: 4px;
	text-shadow: 0 1px 2px #0006;
`;

function ProfileSection() {
	const [activeTab, setActiveTab] = useState('profile');
	const [versionClickCount, setVersionClickCount] = useState(0);
	useScrollToTop([activeTab]);
	const navigate = useNavigate();
	const fetchUser = useUserStore(state => state.fetchUser);
	const user = useUserStore(state => state.user);
	const { theme, toggleTheme } = useThemeContext();
	const isDarkTheme = theme === 'dark';
	const { isSoundEnabled, toggleSound } = useSoundContext();
	const { t, i18n } = useTranslation();

	const tgUser = WebApp.initDataUnsafe?.user;

	// Загрузка данных пользователя
	useEffect(() => {
		const loadUserData = async () => {
			if (!user) {
				await fetchUser();
			}
		};
		loadUserData();
	}, [user, fetchUser]);

	const handleExternalLink = (url) => {
		if (WebApp.platform !== "unknown") {
			WebApp.openLink(url);
		} else {
			window.open(url, '_blank');
		}
	};

	const handleVersionClick = () => {
		const newCount = versionClickCount + 1;
		setVersionClickCount(newCount);

		console.log(`Версия нажата ${newCount} раз`);

		if (newCount >= 10) {
			// Очищаем localStorage
			localStorage.clear();
			console.log('localStorage очищен! Перезагрузите страницу.');

			// Показываем уведомление
			WebApp.showAlert('localStorage очищен! Перезагрузите страницу.');

			// Сбрасываем счетчик
			setVersionClickCount(0);
		}
	};



	const links = [
		{
			id: 'buy-gems',
			title: t('buyTokens', { currency: t('currency_title') }),
			subtitle: t('profile.topUpBalance'),
			icon: <FaShoppingCart size={20} />,
			iconBg: '#5856d6',
			action: () => navigate('/packages')
		},
		{
			id: 'claim-frags',
			title: t('promo.usePromo'),
			subtitle: t('promo.claimFrags') || t('tokens.description', { currency: t('currency_title') }),
			icon: <FaShoppingCart size={20} />,
			iconBg: '#0ea5e9',
			action: () => navigate('/claim-frags')
		},
		{
			id: 'trade',
			title: t('profile.tradeLink'),
			subtitle: t('profile.connectSteam'),
			icon: <FaLink size={20} />,
			iconBg: '#34c759',
			action: () => navigate('/trade-url')
		},
		{
			id: 'community',
			title: `${t('profile.community')} ${APP_NAME}`,
			subtitle: t('profile.joinDiscussion'),
			icon: <FaUsers size={20} />,
			iconBg: '#007aff',
			action: () => handleExternalLink('https://t.me/skinforge')
		},
		// {
		// 	id: 'support',
		// 	title: t('profile.support'),
		// 	subtitle: t('profile.contactSupport'),
		// 	icon: <FaHeadset size={20} />,
		// 	iconBg: '#ff9500',
		// 	action: () => navigate('/support')
		// }
	];

	console.log('user', user)
	return (
		<Container>
			<ProfileHeader>
				<AvatarOuter>
					<AvatarContainer $premium={!!user?.isPremium}>
						{!user ? (
							<SkeletonBlock width="96px" height="96px" />
						) : tgUser?.photo_url ? (
							<Avatar src={tgUser.photo_url} alt="Profile" />
						) : user?.avatarUrl ? (
							<Avatar src={`${IMAGE_BASE_URL}${user.avatarUrl}`} alt="Profile" />
						) : (
							<DefaultAvatar>
								{user?.firstName?.[0] || '?'}
							</DefaultAvatar>
						)}
					</AvatarContainer>
					{user?.isPremium && (
						<VipBadge><FaCrown style={{ marginRight: 3, color: '#fff' }} />VIP</VipBadge>
					)}
				</AvatarOuter>
				<UserName>
					{!user ? <SkeletonBlock width="120px" height="28px" /> : [user?.firstName, user?.lastName].filter(Boolean).join(' ')}
				</UserName>
			</ProfileHeader>

			<LinksSection>
				{links.map((link, index) => (
					<LinkCard
						key={link.id}
						onClick={link.action}
						isFirst={index === 0}
						isLast={index === links.length - 1}
					>
						<IconContainer bg={link.iconBg}>
							{link.icon}
						</IconContainer>
						<LinkInfo>
							<LinkTitle>{link.title}</LinkTitle>
							<LinkSubtitle>{link.subtitle}</LinkSubtitle>
						</LinkInfo>
						<ChevronIcon>
							<IoChevronForward size={20} />
						</ChevronIcon>
					</LinkCard>
				))}
			</LinksSection>

			<Section>
				<SectionTitle>{t('profile.settings')}</SectionTitle>
				<SettingsList>
					<SettingsItem onClick={() => navigate('/language')}>
						<SettingLeft>
							<IoLanguage size={20} />
							<SettingText>{t('profile.language')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<LanguageDisplay>
								{(() => {
									const savedLang = localStorage.getItem('app_language');
									const effectiveLang = String(savedLang || i18n.language || 'en').toLowerCase().split('-')[0];
									const langMap = {
										'en': 'English',
										'ru': 'Русский',
										'uz': "O'zbekcha",
										'uk': 'Українська',
										'tr': 'Türkçe',
										'id': 'Bahasa Indonesia',
										'ar': 'العربية',
										'fr': 'Français',
										'fa': 'فارسی',
										'vi': 'Tiếng Việt',
										'az': 'Azərbaycan',
										'ge': 'ქართული',
										'kk': 'Қазақ тілі',
										'ky': 'Кыргызча',
										'tj': 'Тоҷикӣ',
										'mn': 'Монгол',
										'sk': 'Slovenčina',
										'zh': '中文',
										'pl': 'Polski',
										'kr': '한국어',
										'pt': 'Português'
									};
									return langMap[effectiveLang] || effectiveLang;
								})()}
							</LanguageDisplay>
							<IoChevronForward size={20} />
						</SettingRight>
					</SettingsItem>

					<SettingsItem>
						<SettingLeft>
							<IoVolumeMedium size={20} />
							<SettingText>{t('profile.sound')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<Toggle isActive={isSoundEnabled} onClick={toggleSound} />
						</SettingRight>
					</SettingsItem>

					<SettingsItem onClick={() => navigate('/music')}>
						<SettingLeft>
							<IoMusicalNotes size={20} />
							<SettingText>{t('music.menu')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<IoChevronForward size={20} />
						</SettingRight>
					</SettingsItem>

					<SettingsItem>
						<SettingLeft>
							<IoMoon size={20} />
							<SettingText>{t('profile.darkTheme')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<Toggle isActive={isDarkTheme} onClick={toggleTheme} />
						</SettingRight>
					</SettingsItem>
				</SettingsList>
			</Section>

			<Version onClick={handleVersionClick}>
				{t('common.version')} 3.2.18
			</Version>
		</Container >
	);
}

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;
`;

const ProfileHeader = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 32px;
	padding: 20px 0;
`;

const UserName = styled.h1`
	font-size: 24px;
	font-weight: 600;
	color: ${props => props.theme.text.primary};
	margin: 0;
	text-align: center;
`;

const LinksSection = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 16px;
	overflow: hidden;
	margin-bottom: 24px;
`;

const LinkCard = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	padding: 16px;
	background: none;
	border: none;
	border-bottom: ${props =>
		props.isLast ? 'none' : `1px solid ${props.theme.border}`};
	cursor: pointer;
	transition: background-color 0.2s;

	&:active {
		opacity: 0.7;
	}
`;

const IconContainer = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: ${props => props.bg};
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	margin-right: 16px;
	flex-shrink: 0;
`;

const LinkInfo = styled.div`
	flex: 1;
	text-align: left;
`;

const LinkTitle = styled.div`
	font-size: 16px;
	font-weight: 500;
	color: ${props => props.theme.text.primary};
	margin-bottom: 4px;
`;

const LinkSubtitle = styled.div`
	font-size: 13px;
	color: ${props => props.theme.text.secondary};
`;

const ChevronIcon = styled.div`
	color: ${props => props.theme.text.secondary};
	margin-left: 8px;
	opacity: 0.5;
`;

const SectionTitle = styled.h2`
	font-size: 20px;
	font-weight: 600;
	color: ${props => props.theme.text.primary};
	margin: 0 0 16px 0;
`;

const Version = styled.div`
	text-align: center;
	color: ${props => props.theme.text.secondary};
	font-size: 13px;
	margin-top: 32px;
	cursor: pointer;
	user-select: none;
	transition: opacity 0.2s;
	
	&:active {
		opacity: 0.7;
	}
`;

const Section = styled.div`
	margin-bottom: 24px;
`;

const SettingsList = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 2px 8px ${props => props.theme.type === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.2)'};
`;

const SettingsItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	cursor: pointer;
	color: ${props => props.theme.text.primary};
	background: ${props => props.theme.card.background};
	
	&:not(:last-child) {
		border-bottom: 1px solid ${props => props.theme.border};
	}

	&:active {
		opacity: 0.7;
	}
`;

const SettingLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	color: ${props => props.theme.text.primary};
`;

const SettingText = styled.span`
	font-size: 16px;
	color: ${props => props.theme.text.primary};
`;

const SettingRight = styled.div`
	display: flex;
	align-items: center;
	opacity: 1;
`;

const Toggle = styled.button`
	width: 44px;
	height: 24px;
	background: ${props => props.isActive ? props.theme.primary : props.theme.type === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
	border-radius: 12px;
	border: none;
	position: relative;
	cursor: pointer;
	transition: all 0.2s ease;
	padding: 2px;

	&:after {
		content: '';
		position: absolute;
		left: ${props => props.isActive ? 'calc(100% - 22px)' : '2px'};
		top: 2px;
		width: 20px;
		height: 20px;
		background: white;
		border-radius: 50%;
		transition: all 0.2s ease;
	}
`;

const LanguageDisplay = styled.span`
	color: ${props => props.theme.text.secondary};
	font-size: 14px;
	margin-right: 8px;
`;

export default ProfileSection;